import { Box, Stack, Text, Flex, Loader, Button } from '@mantine/core';
import {
  ChevronRight,
  type Icon as FeatherIcon,
  List,
  Plus,
} from 'react-feather';
import { useGetListsQuery } from '@/features/customListSlice';
import { useClientTerminology } from '@/views/list/NetworkTerms';
import { useSmallScreen } from '@/hooks/useWindowDimension';
import { formatDateRelative } from '@/helpers/date';
import { Icon } from '@/components';

interface CustomListOptionsProps {
  onItemClick: (id: string) => void;
}

export function CustomListOptions({ onItemClick }: CustomListOptionsProps) {
  const { data: lists, isLoading: isListLoading } = useGetListsQuery({
    sortBy: 'name',
    sortOrder: 'asc',
  });
  const intl = useClientTerminology();
  const isSmallScreen = useSmallScreen();

  const renderItem = (
    id: string,
    name: string,
    FeatherIcon: FeatherIcon,
    description?: string
  ) => (
    <Button
      key={id}
      type="button"
      data-testid={`custom-list-item-${id}`}
      variant="transparent"
      fullWidth
      justify="space-between"
      color="onSurface.0"
      size="xl"
      onClick={() => onItemClick(id)}
      p={isSmallScreen ? 'xs' : 'md'}
      style={{
        '--button-hover': 'var(--mantine-color-grayBrand-0)',
      }}
      leftSection={
        <Flex gap="sm" align="center">
          <Box
            bg="grayBrand.0"
            p="xs"
            display="flex"
            style={{ borderRadius: 'var(--mantine-radius-md)' }}
          >
            <Icon Component={FeatherIcon} size={16} />
          </Box>
          <Stack gap={0} mr="auto" align="start">
            <Text fw="bold" maw="25rem" truncate>
              {name}
            </Text>
            {description && (
              <Text c="gray.7" fz="sm">
                {description}
              </Text>
            )}
          </Stack>
        </Flex>
      }
      rightSection={<ChevronRight />}
    ></Button>
  );

  return (
    <Box p="sm" w="100%">
      <Stack gap="xs">
        {renderItem(
          'new',
          intl.formatMessage({ id: 'customList.newList' }),
          Plus
        )}
        {isListLoading && <Loader size="sm" mx="auto" />}
        {lists?.length
          ? lists.map((listItem) =>
              renderItem(
                listItem.id,
                listItem.name ||
                  `${listItem.createdBy} - ${listItem.createdAt}`,
                List,
                listItem.updatedAt
                  ? intl.formatMessage(
                      {
                        id: 'meta.lastEditedDate',
                      },
                      { date: formatDateRelative(listItem.updatedAt) }
                    )
                  : undefined
              )
            )
          : null}

        {!lists?.length && !isListLoading ? (
          <Text size="sm" c="gray.5" mx="auto" component="i">
            {intl.formatMessage({ id: 'customList.noListsAvailable' })}
          </Text>
        ) : null}
      </Stack>
    </Box>
  );
}
